import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import {Room} from '../../data/rooms';
import {deleteRoom} from '../../services/scheduleService'; // You’ll need this API function

interface DeleteRoomModalProps {
    isOpen: boolean;
    onClose: () => void;
    allRooms: Room[];
    refreshRooms: () => Promise<void>;
}

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const DeleteRoomModal: React.FC<DeleteRoomModalProps> = ({
                                                             isOpen,
                                                             onClose,
                                                             allRooms,
                                                             refreshRooms
                                                         }) => {
    const [selectedRoomId, setSelectedRoomId] = useState<string>('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const selectedRoom = allRooms.find(room => room.RoomId === selectedRoomId);

    useEffect(() => {
        if (isOpen) {
            setSelectedRoomId('');
            setError(null);
        }
    }, [isOpen]);

    const handleDelete = async () => {
        if (!selectedRoom) return;

        setIsDeleting(true);
        setError(null);

        try {
            await deleteRoom(selectedRoom);
            await refreshRooms();

            onClose();
        } catch (error: any) {
            console.error('Delete Error:', error);

            let errorMessage = 'Failed to delete room';
            if (error.message.includes('Unauthorized')) {
                errorMessage = 'You are not authorized to delete rooms';
            } else if (error.message.includes('not found')) {
                errorMessage = 'Room not found';
            }

            setError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };


    return (
        <Modal
            open={isOpen}
            onClose={isDeleting ? undefined : onClose}
            aria-labelledby="delete-room-modal-title"
        >
            <Box sx={modalStyle}>
                <Typography id="delete-room-modal-title" variant="h6" component="h2" gutterBottom>
                    Delete Room
                </Typography>

                <FormControl fullWidth margin="dense">
                    <InputLabel>Select Room</InputLabel>
                    <Select
                        value={selectedRoomId}
                        onChange={(e) => setSelectedRoomId(e.target.value)}
                        label="Select Room"
                    >
                        <MenuItem value="" disabled>
                            -- Select a Room --
                        </MenuItem>
                        {allRooms.map((room) => (
                            <MenuItem key={room.RoomId} value={room.RoomId}>
                                {room.Title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedRoom && (
                    <Typography variant="body1" sx={{my: 2}}>
                        Are you sure you want to delete <strong>{selectedRoom.Title}</strong>? This action cannot be
                        undone.
                    </Typography>
                )}

                {error && (
                    <Typography color="error" sx={{mb: 2}}>
                        {error}
                    </Typography>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        startIcon={<CancelIcon/>}
                        onClick={onClose}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className="delete"
                        startIcon={isDeleting ? <CircularProgress size={20} color="inherit"/> : <DeleteIcon/>}
                        onClick={handleDelete}
                        disabled={isDeleting || !selectedRoomId}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};

export default DeleteRoomModal;
